"use client";

import { useMemo, useState } from "react";
import type { PublicCountryDto } from "@/lib/api-client";
import { createTranslator } from "@/lib/localization";
import { registerCustomer } from "@/lib/account-api";

type Props = {
  activeLanguage: string;
  countries: PublicCountryDto[];
  texts?: Record<string, string> | null;
};

type FormState = {
  email: string;
  phoneCountryCode: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  countryCode: string;
  city: string;
  password: string;
  confirmPassword: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FORM: FormState = {
  email: "",
  phoneCountryCode: "",
  phoneNumber: "",
  firstName: "",
  lastName: "",
  countryCode: "",
  city: "",
  password: "",
  confirmPassword: "",
};

export function RegisterForm({ activeLanguage, countries, texts }: Props) {
  const t = createTranslator(texts ?? {});
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailPending, setEmailPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const callingCodeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const country of countries) {
      if (!country.phoneCallingCode || seen.has(country.phoneCallingCode)) continue;
      seen.set(country.phoneCallingCode, country.name);
    }
    return Array.from(seen.entries())
      .map(([callingCode, name]) => ({ callingCode, name }))
      .sort((a, b) => a.callingCode.localeCompare(b.callingCode, undefined, { numeric: true }));
  }, [countries]);

  function updateField<K extends keyof FormState>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof FormState, string>> = {};

    if (!form.email.trim() || !EMAIL_PATTERN.test(form.email.trim())) {
      errors.email = t("validation.email");
    }
    if (!form.phoneCountryCode) {
      errors.phoneCountryCode = t("validation.callingCodeRequired");
    }
    if (!form.phoneNumber.trim()) {
      errors.phoneNumber = t("validation.phoneNumber");
    }
    if (!form.firstName.trim()) {
      errors.firstName = t("validation.required");
    }
    if (!form.lastName.trim()) {
      errors.lastName = t("validation.required");
    }
    if (!form.countryCode) {
      errors.countryCode = t("validation.countryRequired");
    }
    if (!form.city.trim()) {
      errors.city = t("validation.required");
    }
    if (form.password.length < 8) {
      errors.password = t("validation.passwordMinLength");
    }
    if (form.confirmPassword !== form.password) {
      errors.confirmPassword = t("validation.passwordMismatch");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setServerError(null);

    if (!validate()) {
      return;
    }

    setStatus("submitting");
    const result = await registerCustomer({ ...form, languageCode: activeLanguage });

    if (result.ok) {
      setStatus("success");
      setEmailPending(result.data?.emailDeliveryStatus === "NotConfigured");
      setForm(EMPTY_FORM);
      return;
    }

    setStatus("error");
    setServerError(result.data?.message || t("auth.register.genericError"));
  }

  if (status === "success") {
    return (
      <section style={styles.section}>
        <div style={styles.card}>
          <p style={styles.successMessage}>
            {emailPending ? t("auth.register.successEmailPending") : t("auth.register.success")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section style={styles.section}>
      <div style={styles.card}>
        <h1 style={styles.title}>{t("auth.register.title")}</h1>
        <p style={styles.subtitle}>{t("auth.register.subtitle")}</p>

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <Field label={t("auth.register.email")} error={fieldErrors.email}>
            <input
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              style={styles.input}
            />
          </Field>

          <div style={styles.phoneRow}>
            <Field label={t("auth.register.phoneCountryCode")} error={fieldErrors.phoneCountryCode} style={styles.phoneCodeField}>
              <select
                autoComplete="tel-country-code"
                value={form.phoneCountryCode}
                onChange={(e) => updateField("phoneCountryCode", e.target.value)}
                style={styles.input}
              >
                <option value="">{t("auth.register.selectCallingCode")}</option>
                {callingCodeOptions.map((option) => (
                  <option key={option.callingCode} value={option.callingCode}>
                    {option.callingCode} ({option.name})
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t("auth.register.phoneNumber")} error={fieldErrors.phoneNumber} style={styles.phoneNumberField}>
              <input
                type="tel"
                autoComplete="tel-national"
                value={form.phoneNumber}
                onChange={(e) => updateField("phoneNumber", e.target.value)}
                style={styles.input}
              />
            </Field>
          </div>

          <div style={styles.nameRow}>
            <Field label={t("auth.register.firstName")} error={fieldErrors.firstName}>
              <input
                type="text"
                autoComplete="given-name"
                value={form.firstName}
                onChange={(e) => updateField("firstName", e.target.value)}
                style={styles.input}
              />
            </Field>

            <Field label={t("auth.register.lastName")} error={fieldErrors.lastName}>
              <input
                type="text"
                autoComplete="family-name"
                value={form.lastName}
                onChange={(e) => updateField("lastName", e.target.value)}
                style={styles.input}
              />
            </Field>
          </div>

          <Field label={t("auth.register.country")} error={fieldErrors.countryCode}>
            <select
              autoComplete="country"
              value={form.countryCode}
              onChange={(e) => updateField("countryCode", e.target.value)}
              style={styles.input}
            >
              <option value="">{t("auth.register.selectCountry")}</option>
              {countries.map((country) => (
                <option key={country.iso2Code} value={country.iso2Code}>
                  {country.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("auth.register.city")} error={fieldErrors.city}>
            <input
              type="text"
              autoComplete="address-level2"
              value={form.city}
              onChange={(e) => updateField("city", e.target.value)}
              style={styles.input}
            />
          </Field>

          <Field label={t("auth.register.password")} error={fieldErrors.password}>
            <div style={styles.passwordRow}>
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => updateField("password", e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={styles.toggleButton}
                aria-label={showPassword ? t("auth.register.hidePassword") : t("auth.register.showPassword")}
              >
                {showPassword ? "🙈" : "👁"}
              </button>
            </div>
          </Field>

          <Field label={t("auth.register.confirmPassword")} error={fieldErrors.confirmPassword}>
            <div style={styles.passwordRow}>
              <input
                type={showConfirmPassword ? "text" : "password"}
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={(e) => updateField("confirmPassword", e.target.value)}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                style={styles.toggleButton}
                aria-label={showConfirmPassword ? t("auth.register.hidePassword") : t("auth.register.showPassword")}
              >
                {showConfirmPassword ? "🙈" : "👁"}
              </button>
            </div>
          </Field>

          {serverError ? <p style={styles.errorMessage}>{serverError}</p> : null}

          <button type="submit" disabled={status === "submitting"} style={styles.submitButton}>
            {status === "submitting" ? t("auth.register.submitting") : t("auth.register.submit")}
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({
  label,
  error,
  style,
  children,
}: {
  label: string;
  error?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <label style={{ ...styles.field, ...style }}>
      <span style={styles.label}>{label}</span>
      {children}
      {error ? <span style={styles.fieldError}>{error}</span> : null}
    </label>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { marginTop: 16, padding: "0 16px 48px" },
  card: {
    maxWidth: 560,
    margin: "0 auto",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1.5rem",
    background: "#fff",
  },
  title: { margin: "0 0 0.4rem", fontSize: "1.5rem", color: "#0f172a", fontWeight: 700 },
  subtitle: { margin: "0 0 1.2rem", fontSize: "0.9rem", color: "#64748b" },
  form: { display: "grid", gap: 14 },
  phoneRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 },
  phoneCodeField: {},
  phoneNumberField: {},
  nameRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 },
  field: { display: "grid", gap: 4 },
  label: { fontSize: "0.85rem", color: "#334155", fontWeight: 600 },
  input: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0.6rem 0.75rem",
    fontSize: "0.95rem",
    width: "100%",
    boxSizing: "border-box" as const,
  },
  passwordRow: { display: "flex", gap: 6, alignItems: "center" },
  toggleButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#f8fafc",
    padding: "0.5rem 0.7rem",
    cursor: "pointer",
    fontSize: "0.95rem",
  },
  fieldError: { fontSize: "0.78rem", color: "#dc2626" },
  errorMessage: { margin: 0, color: "#dc2626", fontSize: "0.88rem", fontWeight: 600 },
  successMessage: { margin: 0, color: "#15803d", fontSize: "1rem", fontWeight: 600, textAlign: "center" as const },
  submitButton: {
    marginTop: 6,
    border: "none",
    borderRadius: 8,
    background: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    cursor: "pointer",
  },
};
